/* global budgetIsLocked processAjaxConfirmation */
import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import { Formik } from 'formik';
import escape from 'lodash/escape';
import * as Yup from 'yup';

import { budgetTagsErrorMessages } from './itemBudgetTagsApi';

import { apiClient } from 'Helpers/network';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';
import Modal from 'CommonComponents/Modal';
import ModalHeader from 'CommonComponents/ModalHeader';

import useModal from 'Hooks/useModal';

const FIELD_CLASSES = 'tw-min-w-0 tw-rounded-lg tw-bg-input-background tw-text-input-text tw-font-semibold tw-border-0 tw-text-sm tw-py-2 tw-px-3';

const sameId = (a, b) => String(a) === String(b);

const validationSchema = Yup.object({
    codesetId: Yup.string().required(),
    tagId: Yup.string().required(() => $t('Choose an item.')),
    percentage: Yup.number()
        .typeError(() => $t('Enter a percentage.'))
        .required(() => $t('Enter a percentage.'))
        .min(0.01, () => $t('Enter a percentage between 0.01 and 100.'))
        .max(100, () => $t('Enter a percentage between 0.01 and 100.'))
        .test(
            'decimals',
            () => $t('Use at most two decimal places.'),
            (value) => value === undefined || Math.abs(Math.round(value * 100) - value * 100) < 1e-9,
        ),
});

function initialValues(codesets) {
    const codeset = codesets[0];

    return {
        codesetId: codeset ? String(codeset.id) : '',
        tagId: codeset && codeset.items[0] ? String(codeset.items[0].id) : '',
        percentage: '100',
    };
}

function ApplyTagForm({ formik, codesets, targets, skippedCount, saveErrors, onClose }) {
    const { values, errors, touched, isSubmitting, setFieldValue, handleChange, handleSubmit } = formik;
    const codeset = codesets.find((entry) => sameId(entry.id, values.codesetId));
    const fieldError = (touched.tagId && errors.tagId) || (touched.percentage && errors.percentage);

    const changeCodeset = (event) => {
        const next = codesets.find((entry) => sameId(entry.id, event.target.value));
        setFieldValue('codesetId', event.target.value);
        setFieldValue('tagId', next && next.items[0] ? String(next.items[0].id) : '');
    };

    const title = targets.length === 1 ? $t('Apply tag to 1 line') : $t('Apply tag to {{count}} lines', { count: targets.length });

    return (
        <form onSubmit={handleSubmit} noValidate>
            <ModalHeader title={title} close={onClose} />
            <div className="tw-space-y-4">
                <div className="tw-grid tw-grid-cols-[1fr_1.2fr_70px_auto] tw-items-center tw-gap-2">
                    <select
                        aria-label={$t('Codeset')}
                        className={`tw-w-full ${FIELD_CLASSES}`}
                        name="codesetId"
                        value={values.codesetId}
                        onChange={changeCodeset}
                        disabled={isSubmitting}
                    >
                        {codesets.map((entry) => (
                            <option key={entry.id} value={entry.id}>
                                {entry.name}
                            </option>
                        ))}
                    </select>
                    <select
                        aria-label={$t('Item')}
                        className={`tw-w-full ${FIELD_CLASSES}`}
                        name="tagId"
                        value={values.tagId}
                        onChange={handleChange}
                        disabled={isSubmitting}
                    >
                        {(codeset ? codeset.items : []).map((item) => (
                            <option key={item.id} value={item.id}>
                                {item.name}
                            </option>
                        ))}
                    </select>
                    <input
                        aria-label={$t('Percentage')}
                        type="number"
                        min="0.01"
                        max="100"
                        step="0.01"
                        className={`tw-w-full tw-text-end ${FIELD_CLASSES}`}
                        name="percentage"
                        value={values.percentage}
                        onChange={handleChange}
                        disabled={isSubmitting}
                    />
                    <span className="tw-text-sm tw-text-charcoal-75">%</span>
                </div>
                {fieldError && <p className="tw-text-red tw-text-sm tw-mb-0">{fieldError}</p>}
                <p className="tw-text-sm tw-text-charcoal-75 tw-mb-0">
                    {$t(
                        'Adds this tag to each selected line. Existing tags on the lines are kept. Lines already carrying this tag are updated to the % entered.',
                    )}
                </p>
                {skippedCount > 0 && (
                    <Alert type="warning" role="status">
                        {skippedCount === 1
                            ? $t("1 selected row can't carry budget tags and will be left unchanged.")
                            : $t("{{count}} selected rows can't carry budget tags and will be left unchanged.", { count: skippedCount })}
                    </Alert>
                )}
                {saveErrors.length > 0 && (
                    <Alert type="error">
                        <div className="tw-space-y-1">
                            {saveErrors.map((message, index) => (
                                <p key={index} className="tw-mb-0">
                                    {message}
                                </p>
                            ))}
                        </div>
                    </Alert>
                )}
            </div>
            <div className="modal__footer tw-justify-end tw-space-x-1.5">
                <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
                    {$t('Cancel')}
                </Button>
                <Button variant="primary" type="submit" disabled={isSubmitting} loading={isSubmitting}>
                    {$t('Apply')}
                </Button>
            </div>
        </form>
    );
}

/**
 * Bulk "Apply tag" dialog on the department and cost-centre expenditure tables. The
 * table (plain JS, not bundled) opens it with a window CustomEvent
 * `budgettags:apply-open` carrying `{ targets: [{ expense_id, pack_id, name }], skippedCount }`,
 * and hears `budgettags:applied` with the server's `{ added, updated, skipped }` once saved.
 */
export default function BulkApplyBudgetTagModal({ applyUrl, codesets }) {
    const modal = useModal();
    const [opening, setOpening] = useState(null);
    const [saveErrors, setSaveErrors] = useState([]);

    const handleOpen = useCallback(
        (event) => {
            const detail = event.detail || {};
            if (!Array.isArray(detail.targets) || detail.targets.length === 0) return;
            setSaveErrors([]);
            setOpening({ key: Date.now(), targets: detail.targets, skippedCount: Number(detail.skippedCount) || 0 });
            modal.show();
        },
        [modal.show],
    );

    useEffect(() => {
        window.addEventListener('budgettags:apply-open', handleOpen);
        return () => window.removeEventListener('budgettags:apply-open', handleOpen);
    }, [handleOpen]);

    const close = () => {
        modal.hide();
        setOpening(null);
    };

    const save = (values, { setSubmitting }) => {
        setSaveErrors([]);
        apiClient
            .post(applyUrl, {
                tag_id: Number(values.tagId),
                percentage: Number(values.percentage),
                lines: opening.targets.map((target) => ({ expense_id: target.expense_id, pack_id: target.pack_id || null })),
            })
            .then((response) => {
                const result = response.data || {};
                close();
                if (typeof window.showToast === 'function') {
                    const count = (result.added || 0) + (result.updated || 0);
                    const message = count === 1 ? $t('Tag applied to 1 line.') : $t('Tag applied to {{count}} lines.', { count });
                    const hint = Number(result.followed) > 0 ? ` ${$t('Lines that follow their group pick the tag up from it.')}` : '';
                    window.showToast(`${message}${hint}`, 'success');
                    const skipped = Array.isArray(result.skipped) ? result.skipped : [];
                    if (skipped.length > 0) {
                        // showToast renders raw HTML and i18next does not escape interpolations.
                        const names = skipped.map((line) => escape(line.name || `#${line.expense_id}`)).join(', ');
                        window.showToast($t('Left unchanged: {{names}}', { names }), 'warning');
                    }
                }
                window.dispatchEvent(new CustomEvent('budgettags:applied', { detail: result }));
            })
            .catch((error) => setSaveErrors(budgetTagsErrorMessages(error)))
            .finally(() => setSubmitting(false));
    };

    const submit = (values, formik) => {
        typeof budgetIsLocked !== 'undefined' && budgetIsLocked ? processAjaxConfirmation(() => save(values, formik)) : save(values, formik);
    };

    return (
        <Modal id="bulk-apply-budget-tag-modal" modal={modal} innerContainerClasses="tw-max-w-2xl">
            {opening && (
                <Formik key={opening.key} initialValues={initialValues(codesets)} validationSchema={validationSchema} onSubmit={submit}>
                    {(formik) => (
                        <ApplyTagForm
                            formik={formik}
                            codesets={codesets}
                            targets={opening.targets}
                            skippedCount={opening.skippedCount}
                            saveErrors={saveErrors}
                            onClose={close}
                        />
                    )}
                </Formik>
            )}
        </Modal>
    );
}

const rootEl = document.getElementById('BulkApplyBudgetTagModalApp');
if (rootEl) {
    ReactDOM.render(
        <App>
            <BulkApplyBudgetTagModal applyUrl={rootEl.getAttribute('data-apply-url')} codesets={JSON.parse(rootEl.getAttribute('data-codesets') || '[]')} />
        </App>,
        rootEl,
    );
}
